'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { MdCampaign, MdBuild, MdWarningAmber, MdNewReleases } from 'react-icons/md'

import {
    acknowledgeAnnouncement,
    getPendingAnnouncements,
    type PendingAnnouncement,
    type SystemAnnouncementKind,
} from '@/lib/api/system-announcements'
import { useAuth } from '@/store/hooks'

/**
 * Avisos publicados pelo painel MASTER, exibidos a quem ainda não os viu.
 *
 * Quem é MASTER não recebe: é quem publica, não o público. Sem isso o modal
 * abria na cara de quem acabou de clicar em "Publicar".
 *
 * Por que a consulta acontece na SUBIDA e não só pelo websocket: quem estava
 * offline no momento da publicação precisa receber ao entrar — amanhã, ou na
 * semana que vem. Se o único caminho fosse o evento em tempo real, todo evento
 * perdido viraria aviso perdido, e o recurso serviria só para quem já estava
 * com a tela aberta — exatamente o problema do grupo de WhatsApp que ele veio
 * substituir.
 *
 * Um aviso por vez, em ordem de publicação: dois modais empilhados não se leem.
 */

const KIND_STYLE: Record<
    SystemAnnouncementKind,
    { icon: typeof MdCampaign; accent: string; label: string }
> = {
    INFO: { icon: MdCampaign, accent: 'text-info', label: 'Comunicado' },
    WARNING: { icon: MdWarningAmber, accent: 'text-warning', label: 'Atenção' },
    MAINTENANCE: { icon: MdBuild, accent: 'text-warning', label: 'Manutenção' },
    RELEASE: { icon: MdNewReleases, accent: 'text-secondary', label: 'Novidades' },
}

export default function SystemAnnouncementModal() {
    const { user, isAuthenticated, token } = useAuth()
    const [pending, setPending] = useState<PendingAnnouncement[]>([])
    const [saving, setSaving] = useState(false)
    // Evita duas buscas simultâneas quando o socket reconecta junto com o foco.
    const loadingRef = useRef(false)

    const load = useCallback(async () => {
        // MASTER publica, não recebe — o backend já devolve vazio, e parar aqui
        // evita o pedido inútil a cada publicação.
        // ⚠️ Exige o TOKEN, não só o usuário. Este componente mora no `App.tsx`, FORA
        // da área autenticada, e o `user` volta do armazenamento persistido antes de o
        // token estar pronto. Nessa janela a chamada saía sem `Authorization` e o
        // backend logava `UnauthorizedException: Token não fornecido` a cada
        // recarregamento de página.
        if (!isAuthenticated || !token) return
        // MASTER publica, não recebe — o backend já devolve vazio, e parar aqui
        // evita o pedido inútil a cada publicação.
        if (!user?.id || user?.userType === 'MASTER' || loadingRef.current) return
        loadingRef.current = true
        try {
            const response = await getPendingAnnouncements()
            if (response.success) setPending(response.result ?? [])
        } finally {
            loadingRef.current = false
        }
        // `token` nas dependências: é o que faz a busca rodar assim que ele chega,
        // e não ficar para a próxima navegação.
    }, [user?.id, user?.userType, isAuthenticated, token])

    useEffect(() => {
        void load()

        // Publicação em tempo real. É o caminho que importa para aviso urgente:
        // quem usa o sistema o dia inteiro não recarrega a página, e sem isto o
        // aviso das 14h só seria lido no dia seguinte — o mesmo defeito do grupo
        // de WhatsApp que este recurso veio substituir.
        const onPublished = () => void load()
        window.addEventListener('app:announcement-published', onPublished)

        // Reconexão do socket: o backend reiniciou (deploy), e pode ter havido
        // publicação enquanto a conexão estava caída.
        window.addEventListener('app:socket-reconnected', onPublished)

        // Voltar ao foco: rede oscilando pode ter feito o evento se perder.
        // Todos os caminhos chamam a MESMA consulta, que é a fonte da verdade.
        const onVisible = () => {
            if (document.visibilityState === 'visible') void load()
        }
        document.addEventListener('visibilitychange', onVisible)

        return () => {
            window.removeEventListener('app:announcement-published', onPublished)
            window.removeEventListener('app:socket-reconnected', onPublished)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [load])

    const current = pending[0]
    if (!current) return null

    const style = KIND_STYLE[current.kind] ?? KIND_STYLE.INFO
    const Icon = style.icon

    const dismiss = async () => {
        setSaving(true)
        try {
            // Registra a leitura mesmo quando o aviso não exige confirmação: o
            // que não pode acontecer é o aviso voltar a cada carregamento.
            const response = await acknowledgeAnnouncement(current.id)
            if (response.success) {
                setPending(rest => rest.slice(1))
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <dialog className="modal modal-open z-[9998]">
            <div className="modal-box !max-w-xl">
                <div className="flex items-start gap-3 mb-4">
                    <Icon className={`text-3xl flex-shrink-0 ${style.accent}`} />
                    <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wide text-base-content/50">
                            {style.label}
                        </p>
                        <h3 className="text-lg font-bold leading-snug">{current.title}</h3>
                    </div>
                </div>

                {/* `whitespace-pre-wrap`: o corpo é texto livre e as quebras de
                    linha que quem escreveu pôs são parte da mensagem. */}
                <div className="text-sm leading-relaxed whitespace-pre-wrap text-base-content/80 max-h-[60vh] overflow-y-auto">
                    {current.body}
                </div>

                <div className="modal-action items-center">
                    {pending.length > 1 && (
                        <span className="text-xs text-base-content/50 mr-auto">
                            {pending.length - 1} aviso(s) depois deste
                        </span>
                    )}
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={dismiss}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            'Entendi'
                        )}
                    </button>
                </div>
            </div>
            {/*
                Sem fechar clicando fora, de propósito: o aviso existe porque o
                grupo de WhatsApp não é lido, e um modal que some com um clique
                distraído reproduz o mesmo problema. A saída é o botão, que é o
                que registra a leitura.
            */}
        </dialog>
    )
}
